'use client'

import { Box, Container, Grid, styled, Tab, Tabs, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

import { USPBar } from '@/components/USPBar'
import { BAR_PROPS } from '@/config/BarProps'
import { isB2B } from '@/helpers/isB2B'
import { useSiteContext } from '@/hooks/useSiteContext'
import { useTenant } from '@/hooks/useTenant'
import { useTranslations } from '@/hooks/useTranslations'
import { getTenantMarkets } from '@/lib/tenant/getTenantMarkets'
import { colors } from '@/themes/commerce/colors'

import type { FC } from 'react'
import type { SiteContextModeEnum } from '@/enums/SiteContextModeEnum'
import type { Link as SanityLink } from '@/gen/sanity/schema'

const ContextBarWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.body,

  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  borderBottomColor: theme.palette.grey[200],
}))

interface Props {
  usps?: SanityLink[]
}

export const ContextBar: FC<Props> = ({ usps }) => {
  const { siteContext, setSiteContext } = useSiteContext()
  const { translate } = useTranslations()
  const { data: session } = useSession()
  const queryClient = useQueryClient()
  const tenant = useTenant()

  const tenantSites = getTenantMarkets(tenant.id)
  const activeSite = tenantSites.includes(siteContext) ? siteContext : tenantSites[0]

  const handleChange = (_event: React.SyntheticEvent, newValue: SiteContextModeEnum) => {
    setSiteContext(newValue)
    queryClient.invalidateQueries({ refetchType: 'active' })
  }

  return (
    <ContextBarWrapper
      sx={{
        visibility: {
          xs: 'hidden',
          md: 'visible',
        },
        height: {
          xs: 0,
          md: 'auto',
        },
      }}
    >
      <Container>
        <Grid container sx={{ ...BAR_PROPS.sx, flexWrap: 'wrap' }}>
          <Grid item lg="auto">
            <Box display="flex" alignItems="center" sx={{ minHeight: '47px' }}>
              {!session && (
                <Box sx={{ marginTop: '-1px' }} mr={3}>
                  <Tabs
                    key={activeSite}
                    value={activeSite}
                    onChange={handleChange}
                    aria-label="Shop context switch"
                    sx={(theme) => ({
                      '& .MuiTabs-indicator': {
                        right: 'initial',
                        left: 0,
                        backgroundColor: 'transparent',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: theme.spacing(2),
                          left: 0,
                          width: theme.spacing(0.5),
                          height: theme.spacing(3),
                          backgroundColor: 'transparent',
                          borderRadius: `0px 4px 4px 0px`,
                        },
                      },
                    })}
                  >
                    {tenantSites.map((site) => (
                      <Tab
                        key={site}
                        data-testid={`contextBar-tab-${site}`}
                        sx={(theme) => ({
                          textTransform: 'capitalize',
                          px: 1,
                          backgroundColor: theme.palette.background.default,
                          color: theme.palette.text.primary,
                          fontWeight: 400,
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.background.pale,
                          },
                        })}
                        value={site}
                        label={translate(`siteContext.${site}`)}
                        tabIndex={1}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}
              <Typography variant="body1" sx={{ color: colors.text.placeholder, whiteSpace: 'nowrap' }}>
                {translate(isB2B(activeSite) ? 'siteContext.vat_info.excluding' : 'siteContext.vat_info.including')}
              </Typography>
            </Box>
          </Grid>
          <Grid item lg="auto">
            <USPBar usps={usps} />
          </Grid>
        </Grid>
      </Container>
    </ContextBarWrapper>
  )
}
